import { createContext, PropsWithChildren, useContext, useMemo } from "react";

import { tokens, useMediaQuery } from "~/ui";

type UIContextState = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  defaultColor: string;
};

const UIContext = createContext<UIContextState>({
  isMobile: false,
  isTablet: false,
  isDesktop: false,
  defaultColor: "",
});

export const getUIProviderViewportConfig = () => ({
  mobileQuery: `(max-width: ${tokens.breakpoint.sm})`,
  tabletQuery: `(min-width: ${tokens.breakpoint.sm}) and (max-width: ${tokens.breakpoint.md})`,
  desktopQuery: `(min-width: ${tokens.breakpoint.md})`,
  defaultColor: tokens.color.action.primary,
});

export const UIProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const viewportConfig = getUIProviderViewportConfig();

  const isMobile = useMediaQuery(viewportConfig.mobileQuery);
  const isTablet = useMediaQuery(viewportConfig.tabletQuery);
  const isDesktop = useMediaQuery(viewportConfig.desktopQuery);

  const value = useMemo<UIContextState>(
    () => ({
      isMobile,
      isTablet,
      isDesktop,
      defaultColor: viewportConfig.defaultColor,
    }),
    [isMobile, isTablet, isDesktop, viewportConfig.defaultColor],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => {
  return useContext(UIContext);
};
